import dynamic from "next/dynamic";

import Link from "next/link";
import { useSelector } from "react-redux";

import { isValidLogoImageType } from "@helpers/store";

import MultimediaWithFlag from "@sharedComponents/multimedia";

import { useCreateShopContext } from "@contexts/create-shop-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";
import { useGuideTourContext } from "@contexts/guide-tour";
import { useStoreContext } from "@components/store/provider";

import { getIsPricelessShop } from "@selectors/store";

import {
  getStoreIsWhitelabeled,
  getStoreNumber,
  getStoreLogo,
  getStoreName,
  getStoreLogoType,
  getIsStoreDarkTheme,
  getIsKitStore,
} from "@store/selectors/store";
import { getReceivedItemsCount } from "@selectors/cart";

import { getStoreBasePath } from "@constants/routes";
import { SHOP_LOGO_TYPE, PNG_LOGO_TYPE } from "@constants/logo";
import { BYSTADIUM_URL } from "@stadium/helpers/app-routes";

import WhiteImgPoweredByLogo from "@images/svg/powered-by-stadium-white.svg";
import DarkImgPoweredByLogo from "@images/svg/powered-by-stadium.svg";
import stadiumLogo from "@images/svg/stadium-logo.svg";

import styles from "./main.module.scss";

const EditStoreLogo = dynamic(() => import("@components/dummy-store/edit-store-logo"));

const BrandLogo = ({ className = "" }) => {
  const { countryCode, lang, storeCode, workspaceSlug, isNewRoute } = useStoreContext();

  const logoImage = useSelector(getStoreLogo);
  const logoImageType = useSelector(getStoreLogoType);
  const storeName = useSelector(getStoreName);
  const storeNumber = useSelector(getStoreNumber);
  const storeIsWhitelabeled = useSelector(getStoreIsWhitelabeled);
  const isStoreDarkTheme = useSelector(getIsStoreDarkTheme);
  const isKitStore = useSelector(getIsKitStore);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const receivedItemsCount = useSelector(getReceivedItemsCount);

  const { isNewShopCreationFlow } = useCreateShopContext();
  const { isSwagCreationShopFlow } = useCreateSwagContext();
  const { guideTourSteps } = useGuideTourContext();

  const { step1 = {} } = guideTourSteps;

  const homePageRoute = getStoreBasePath({
    countryCode,
    lang,
    storeCode,
    storeNumber,
    workspaceSlug,
    isNewShopCreationFlow,
    isSwagCreationShopFlow,
    isNewRoute,
  });

  const renderShopLogo = () => {
    if (logoImage && (isValidLogoImageType(logoImageType) || isNewShopCreationFlow)) {
      /* eslint-disable @next/next/no-img-element */
      // NOTE: As logo aspect ratio is not fixed thats why we decided to use image tag
      const logoClassName = SHOP_LOGO_TYPE[logoImageType || PNG_LOGO_TYPE].className;

      return (
        <Link className={styles.navbarBrand} href={homePageRoute} prefetch={false}>
          <img src={logoImage} className={`${styles.storeLogo} ${logoClassName}`} alt={storeName} />
        </Link>
      );
    } else if (isKitStore) {
      return (
        <MultimediaWithFlag type="image" src={stadiumLogo} width={178} height={32} alt="Stadium" />
      );
    } else {
      const logoClass = storeName?.length > 12 ? styles.twoLineLogoName : styles.singleLineLogoName;
      return (
        <p className={styles.storeLogoContainer}>
          <Link
            href={homePageRoute}
            passHref
            className={`${styles.storeLogoName} ${logoClass}`}
            prefetch={false}
          >
            {storeName}
          </Link>
        </p>
      );
    }
  };

  const poweredByLogo = () => {
    const poweredByLogoRoute = isNewShopCreationFlow ? homePageRoute : BYSTADIUM_URL;
    return (
      <Link
        href={poweredByLogoRoute}
        className={styles.powerLogo}
        target={isNewShopCreationFlow ? "" : "_blank"}
        prefetch={false}
      >
        <MultimediaWithFlag
          type="image"
          src={isStoreDarkTheme ? WhiteImgPoweredByLogo : DarkImgPoweredByLogo}
          isNextImage={false}
          alt="Powered by Stadium"
        />
      </Link>
    );
  };

  const isPricelessRedemption = isPricelessShop && receivedItemsCount > 0;

  return (
    <div
      className={`${styles.storeLogoSection} ${className} ${
        isPricelessRedemption ? styles.logoForPriceLess : ""
      } ${isNewShopCreationFlow ? styles.editAbleMode : ""}`}
    >
      {isNewShopCreationFlow && <EditStoreLogo guideTourStep={step1} />}
      {renderShopLogo()} {!storeIsWhitelabeled && poweredByLogo()}
    </div>
  );
};

export default BrandLogo;
